// src/utils/request-status-utils.ts
import { RequestStatus, RequestType, TaskPriority } from '../types/api';

// Labels per gli status delle richieste (API reale)
export const REQUEST_STATUS_LABELS: Record<RequestStatus, string> = {
  [RequestStatus.New]: 'Nuova',
  [RequestStatus.InProgress]: 'In Lavorazione',
  [RequestStatus.PendingCompletion]: 'In Attesa Completamento',
  [RequestStatus.Completed]: 'Completata',
  [RequestStatus.Cancelled]: 'Annullata',
  [RequestStatus.Suspended]: 'Sospesa',
  [RequestStatus.PreCampionatura]: 'Pre-Campionatura',
  [RequestStatus.PreCampionaturaCompleted]: 'Pre-Campionatura Completata'
};

// Colori per gli status delle richieste (API reale)
export const REQUEST_STATUS_COLORS: Record<RequestStatus, string> = {
  [RequestStatus.New]: 'blue',
  [RequestStatus.InProgress]: 'orange',
  [RequestStatus.PendingCompletion]: 'yellow',
  [RequestStatus.Completed]: 'green',
  [RequestStatus.Cancelled]: 'red',
  [RequestStatus.Suspended]: 'gray',
  [RequestStatus.PreCampionatura]: 'grape',
  [RequestStatus.PreCampionaturaCompleted]: 'indigo'
};

/**
 * True se la richiesta è in una delle fasi di Pre-Campionatura (solo Campionatura).
 */
export function isInPreCampionaturaPhase(status?: RequestStatus): boolean {
  return status === RequestStatus.PreCampionatura || status === RequestStatus.PreCampionaturaCompleted;
}

/**
 * Stati validi per una tipologia di richiesta. Gli stati di Pre-Campionatura esistono
 * solo per la Campionatura (le richieste legacy senza questa fase restano comunque
 * visibili tramite gli stati standard, es. New/InProgress).
 */
export function getStatusesForType(requestType?: RequestType): RequestStatus[] {
  const isCampionatura = requestType === RequestType.Campionatura || (requestType as unknown) === 'Campionatura';
  const base = [
    RequestStatus.New,
    RequestStatus.InProgress,
    RequestStatus.PendingCompletion,
    RequestStatus.Suspended,
    RequestStatus.Completed,
    RequestStatus.Cancelled
  ];
  if (!isCampionatura) return base;
  return [RequestStatus.PreCampionatura, RequestStatus.PreCampionaturaCompleted, ...base];
}

/**
 * Indica se una richiesta ha una "data di chiusura" (CompletedAt) valorizzata/modificabile:
 * - richieste completate (qualsiasi tipo) → data di completamento
 * - richieste Gamma sospese (chiusura definitiva) → data di sospensione
 */
export function hasClosureDate(status?: RequestStatus, requestType?: RequestType): boolean {
  const isGamma = requestType === RequestType.Gamma || (requestType as unknown) === 'Gamma';
  return status === RequestStatus.Completed ||
    (status === RequestStatus.Suspended && isGamma);
}

// Labels per i tipi di richiesta (API reale)
export const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  [RequestType.Bozzetto]: 'Bozzetto',
  [RequestType.Campionatura]: 'Campionatura',
  [RequestType.Produzione]: 'Produzione',
  [RequestType.Gamma]: 'Gamma'
};

// Colori per i tipi di richiesta
export const REQUEST_TYPE_COLORS: Record<RequestType, string> = {
  [RequestType.Bozzetto]: 'cyan',
  [RequestType.Campionatura]: 'orange',
  [RequestType.Produzione]: 'green',
  [RequestType.Gamma]: 'violet'
};

// Labels per le priorità dei task
export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  [TaskPriority.Low]: 'Bassa',
  [TaskPriority.Medium]: 'Media',
  [TaskPriority.High]: 'Alta',
  [TaskPriority.Critical]: 'Critica'
};

// Colori per le priorità dei task
export const TASK_PRIORITY_COLORS: Record<TaskPriority, string> = {
  [TaskPriority.Low]: 'gray',
  [TaskPriority.Medium]: 'blue',
  [TaskPriority.High]: 'orange',
  [TaskPriority.Critical]: 'red'
};

// Utility per ottenere il nome utente da assegnazione
export const getAssignedUserDisplay = (request: { assignedToUserId?: string; assignedToUserName?: string }) => {
  if (!request.assignedToUserId) return 'Non assegnato';
  return request.assignedToUserName || `User ID: ${request.assignedToUserId}`;
};

// Utility per formattare date ISO
export const formatApiDate = (dateString?: string) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('it-IT');
  } catch {
    return '-';
  }
};